/**
 * Environment variable validation and access for the web app.
 *
 * All `VITE_*` env vars should be read through this module so that:
 * 1. There is a single source of truth with sensible defaults.
 * 2. Missing **required** vars surface as a clear startup error.
 * 3. Every consumer gets the same value (no scattered `?? "http://..."` fallbacks).
 *
 * ## VITE_API_URL
 *
 * When **unset** (the default for local dev), `API_URL` resolves to `""` (empty
 * string).  This makes every `fetch("${API_URL}/api/...")` call a **relative,
 * same-origin request** which the Vite dev-server proxy forwards to the API
 * backend (see `vite.config.ts` `server.proxy`).
 *
 * Set `VITE_API_URL` only when the frontend is served from a **different origin**
 * than the API (e.g. a production CDN).
 *
 * ## Adding a new variable
 *
 * 1. Add a `readonly` field to `ImportMetaEnv` in `vite-env.d.ts`.
 * 2. Add a line here that reads it with a default.
 * 3. If it is truly required at runtime, add it to `REQUIRED_VARS`.
 */

// ============================================================================
// Raw reads with defaults
// ============================================================================

/**
 * API base URL.  Empty string = same-origin (Vite proxy in dev).
 *
 * Only set `VITE_API_URL` when the frontend is deployed to a different origin
 * from the API server (e.g. production CDN).
 */
export const API_URL: string = import.meta.env.VITE_API_URL ?? "";

/**
 * Whether to use the custom SSE-based agent instead of CopilotKit.
 */
export const USE_CUSTOM_AGENT: boolean =
  import.meta.env.VITE_USE_CUSTOM_AGENT === "true";

/**
 * Whether Microsoft OAuth sign-in is enabled.
 *
 * When `false` (the default), the "Sign in with Microsoft" button is hidden
 * from the login page and `signInWithMicrosoft()` becomes a no-op.
 *
 * To enable, set `VITE_AUTH_MICROSOFT_ENABLED=true` **and** ensure the API
 * also has `AUTH_MICROSOFT_ENABLED=true` with valid Azure credentials.
 */
export const AUTH_MICROSOFT_ENABLED: boolean =
  import.meta.env.VITE_AUTH_MICROSOFT_ENABLED === "true";

/**
 * Opt-in: connect the browser **directly** to the Electric sync service
 * (bypassing the API proxy).  Useful during development when you want to
 * inspect Electric traffic separately.
 *
 * When set, this should be the full Electric origin, e.g.
 * `VITE_ELECTRIC_DIRECT_URL=http://localhost:3000`.
 *
 * When **unset** (the default), Electric shapes are fetched through the API
 * proxy path (`/api/electric`) which the Vite dev-server forwards.
 */
export const ELECTRIC_DIRECT_URL: string | undefined =
  import.meta.env.VITE_ELECTRIC_DIRECT_URL;

// ============================================================================
// OpenTelemetry
// ============================================================================

/**
 * When `"true"`, the OpenTelemetry SDK is not initialised at all.
 */
export const OTEL_DISABLED: boolean =
  import.meta.env.VITE_OTEL_DISABLED === "true";

/**
 * OTLP/HTTP endpoint for trace export.
 *
 * When **unset** (the default), the `BatchSpanProcessor` is still registered
 * but no exporter is attached, so spans are silently dropped.  This keeps the
 * tracing API available for instrumentation code without requiring a collector
 * in every environment.
 */
export const OTEL_EXPORTER_OTLP_ENDPOINT: string | undefined =
  import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT || undefined;

// ============================================================================
// Validation
// ============================================================================

/**
 * Variables that **must** be set for the app to function.
 *
 * Currently none are strictly required because the defaults cover local
 * development.  Add entries here when a production deployment truly needs
 * a value that has no safe default.
 *
 * Format: `[envVarName, humanDescription]`
 */
const REQUIRED_VARS: ReadonlyArray<[string, string]> = [
  // Example:
  // ["VITE_API_URL", "Base URL of the API server (e.g. https://api.example.com)"],
];

/**
 * Validate that all required `VITE_*` env vars are present.
 *
 * Call this once from the app entrypoint (`main.tsx`).  It throws a
 * developer-friendly error listing every missing variable.
 */
export function validateEnv(): void {
  const missing: string[] = [];

  for (const [name, description] of REQUIRED_VARS) {
    const value = (import.meta.env as Record<string, string | undefined>)[name];
    if (value === undefined || value === "") {
      missing.push(`  - ${name}: ${description}`);
    }
  }

  if (missing.length > 0) {
    throw new Error(
      [
        "Missing required environment variables:",
        ...missing,
        "",
        "Create / update your .env file (see .env.example) and restart the dev server.",
      ].join("\n")
    );
  }
}
